import { Environment, getCurrentEnvironment } from '@/utils/environment';

// Ported from the Ember UI (`app/utils/billing/products.ts`). Ember keyed the
// product-id set off `config.cioEnvironment`; Hydra only distinguishes
// development/standalone/production, so we use the production set in production
// and the sandbox set everywhere else (the only difference is Monthly Active Users).

export type ProductId = string;

export const ProductName = {
  // Journeys
  Basic: 'Basic',
  Essentials: 'Essentials',
  Legacy: 'Legacy',
  Premium: 'Premium',
  Enterprise: 'Enterprise',
  InternalFreebie: 'Internal freebie',
  Journeys: 'Journeys',
  JourneysMessageOverages: 'Journeys - Overages - messages',
  JourneysCustomersOverages: 'Journeys - Overages - customers',
  JourneysProfilesOverages: 'Journeys - Overages - profiles',
  JourneysObjectsOverages: 'Journeys - Overages - Objects',
  StartupPlus: 'Startup Plus',
  StartupEssentials: 'Startup Essentials',
  StartupBasic: 'Startup Basic',
  PartnerAccount: 'Partner account',
  LaunchCSM: 'Launch CSM',
  EnterpriseStrategicTechnicalSupport: 'Enterprise Strategic & Technical Support',
  DedicatedIP: 'Dedicated IP',
  DedicatedCSM: 'Dedicated CSM',
  MonthlyActiveUsers: 'Monthly Active Users',

  // Parcel
  ParcelPro: 'ParcelPro',
  ParcelCommunity: 'Parcel Community',
  ParcelBusiness: 'Parcel Business',
  ParcelInboxPreviews: 'Parcel - Inbox Previews',
  ParcelAnalytics: 'Parcel - Analytics',
  ParcelInboxPreviewsOverages: 'Parcel - Overages - Inbox Previews',
  ParcelAnalyticsOverages: 'Parcel - Analytics overages',

  // Pipelines
  CDP: 'CDP',
  CDPStarter: 'CDP Starter',
  CDPPremium: 'CDP Premium',
  CDPApiCallsOverages: 'CDP - Overages - API calls',
} as const;
export type ProductName = (typeof ProductName)[keyof typeof ProductName];

export const BASE_PRODUCT_NAMES = [
  ProductName.Basic,
  ProductName.Essentials,
  ProductName.Legacy,
  ProductName.Premium,
  ProductName.Enterprise,
  ProductName.InternalFreebie,
  ProductName.Journeys,
  ProductName.StartupPlus,
  ProductName.StartupEssentials,
  ProductName.StartupBasic,
  ProductName.PartnerAccount,
  ProductName.CDP,
  ProductName.CDPStarter,
  ProductName.CDPPremium,
] as const;

type EnvironmentProductSet = Record<ProductName, ProductId>;

const ORDWAY_SANDBOX_PRODUCT_IDS: EnvironmentProductSet = {
  [ProductName.Basic]: 'P-00016',
  [ProductName.Essentials]: 'P-00042',
  [ProductName.Legacy]: 'P-00015',
  [ProductName.Premium]: 'P-00039',
  [ProductName.Enterprise]: 'P-00006',
  [ProductName.InternalFreebie]: 'P-00087',
  [ProductName.Journeys]: 'P-00027', // Free Trial
  [ProductName.JourneysMessageOverages]: 'P-00031',
  [ProductName.JourneysCustomersOverages]: 'P-00030',
  [ProductName.JourneysProfilesOverages]: 'P-00105',
  [ProductName.JourneysObjectsOverages]: 'P-00033',
  [ProductName.StartupPlus]: 'P-00012',
  [ProductName.StartupEssentials]: 'P-00013',
  [ProductName.StartupBasic]: 'P-00014',
  [ProductName.PartnerAccount]: 'P-00092',
  [ProductName.LaunchCSM]: 'P-00017',
  [ProductName.EnterpriseStrategicTechnicalSupport]: 'P-00098',
  [ProductName.DedicatedIP]: 'P-00084',
  [ProductName.DedicatedCSM]: 'P-00010',
  [ProductName.MonthlyActiveUsers]: 'P-00287',
  [ProductName.ParcelPro]: 'P-00024',
  [ProductName.ParcelCommunity]: 'P-00025',
  [ProductName.ParcelBusiness]: 'P-00023',
  [ProductName.ParcelInboxPreviews]: 'P-00022',
  [ProductName.ParcelInboxPreviewsOverages]: 'P-00036',
  [ProductName.ParcelAnalytics]: 'P-00021',
  [ProductName.ParcelAnalyticsOverages]: 'P-00035',
  [ProductName.CDP]: 'P-00028',
  [ProductName.CDPStarter]: 'P-00018',
  [ProductName.CDPPremium]: 'P-00019',
  [ProductName.CDPApiCallsOverages]: 'P-00032',
} as const;

const ORDWAY_PRODUCTION_PRODUCT_IDS: EnvironmentProductSet = {
  ...ORDWAY_SANDBOX_PRODUCT_IDS,
  [ProductName.MonthlyActiveUsers]: 'P-00194',
};

export function ordwayProductIds(): EnvironmentProductSet {
  if (getCurrentEnvironment() === Environment.PRODUCTION) {
    return ORDWAY_PRODUCTION_PRODUCT_IDS;
  }

  return ORDWAY_SANDBOX_PRODUCT_IDS;
}

export function isJourneysRelated(ordwayProductId: ProductId): boolean {
  const journeysProductNames = [
    ProductName.Basic,
    ProductName.Essentials,
    ProductName.Legacy,
    ProductName.Premium,
    ProductName.InternalFreebie,
    ProductName.Journeys,
    ProductName.JourneysMessageOverages,
    ProductName.JourneysCustomersOverages,
    ProductName.JourneysProfilesOverages,
    ProductName.JourneysObjectsOverages,
    ProductName.StartupPlus,
    ProductName.StartupEssentials,
    ProductName.StartupBasic,
    ProductName.PartnerAccount,
    ProductName.LaunchCSM,
    ProductName.EnterpriseStrategicTechnicalSupport,
    ProductName.DedicatedIP,
    ProductName.DedicatedCSM,
    ProductName.MonthlyActiveUsers,
  ];

  const productMap = ordwayProductIds();
  return journeysProductNames.some((name) => productMap[name] === ordwayProductId);
}

export function isParcelRelated(ordwayProductId: ProductId): boolean {
  const parcelProductNames = [
    ProductName.ParcelPro,
    ProductName.ParcelCommunity,
    ProductName.ParcelBusiness,
    ProductName.ParcelInboxPreviews,
    ProductName.ParcelInboxPreviewsOverages,
    ProductName.ParcelAnalytics,
    ProductName.ParcelAnalyticsOverages,
  ];

  const productMap = ordwayProductIds();
  return parcelProductNames.some((name) => productMap[name] === ordwayProductId);
}

export function isCdpRelated(ordwayProductId: ProductId): boolean {
  const cdpProductNames = [
    ProductName.CDP,
    ProductName.CDPStarter,
    ProductName.CDPPremium,
    ProductName.CDPApiCallsOverages,
  ];

  const productMap = ordwayProductIds();
  return cdpProductNames.some((name) => productMap[name] === ordwayProductId);
}

export function baseOrdwayProductIds(): ProductId[] {
  const productMap = ordwayProductIds();
  return BASE_PRODUCT_NAMES.map((name) => productMap[name]);
}

export function productNameForId(id: ProductId): ProductName | undefined {
  const productIds = ordwayProductIds();
  const entries = Object.entries(productIds).map(([name, productId]) => [productId, name]);

  return Object.fromEntries(entries)[id];
}

export function descriptionForProduct(id: ProductId): string {
  const productName = productNameForId(id);

  switch (productName) {
    case ProductName.Essentials:
      return 'For small companies who are gaining momentum.';
    case ProductName.Premium:
      return 'For end-to-end marketing for high-growth companies.';
    case ProductName.Enterprise:
      return 'For larger companies scaling lifecycle marketing.';
    case ProductName.StartupPlus:
      return 'Startup +Plus includes all basic features plus additional technical and onboarding support';
    case ProductName.StartupEssentials:
      return 'Startup essentials includes everything you need to start sending messages to your customers';
    case ProductName.StartupBasic:
      return 'Startup basic includes everything you need to start sending messages to your customers';
    case ProductName.CDPStarter:
      return 'Centralize customer data';
    case ProductName.CDPPremium:
      return 'A fully-featured CDP that scales';
    default:
      return '';
  }
}
